"use client";

import { useAuth } from "@clerk/nextjs";
import { ConvexProviderWithAuth, useConvexAuth } from "convex/react";
import React, { createContext, Suspense, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { ReactNode } from "react";

const RecoveryEpochContext = createContext(0);

// Under Clerk v7's `<ClerkProvider dynamic>`, useAuth() suspends on the
// request-time auth promise, so calling it in this app-wide provider would
// suspend every route's static shell. A leaf bridge behind its own <Suspense>
// reads Clerk and publishes a snapshot instead. Until it commits the snapshot
// is null, which Convex sees as loading, never as signed out.
type ClerkAuth = ReturnType<typeof useAuth>;
// Only whether the session token is already Convex-audienced is read from the
// claims, so the snapshot carries that boolean rather than the claims object,
// which is new on every Clerk token refresh.
type ClerkAuthSnapshot = Pick<ClerkAuth, "isLoaded" | "isSignedIn" | "getToken" | "orgId" | "orgRole" | "userId"> & {
  audIsConvex: boolean;
};
const ClerkAuthSnapshotContext = createContext<ClerkAuthSnapshot | null>(null);

function ClerkAuthBridge({
  onAuth,
}: {
  onAuth: (snapshot: ClerkAuthSnapshot) => void;
}) {
  const { isLoaded, isSignedIn, getToken, orgId, orgRole, userId, sessionClaims } = useAuth();
  // Clerk may hand out a new getToken per render; a stable wrapper keeps that
  // from re-running setAuth.
  const getTokenRef = useRef(getToken);
  const stableGetToken = useCallback<ClerkAuth["getToken"]>((options) => getTokenRef.current(options), []);
  const audIsConvex = sessionClaims?.aud === "convex";
  // Before the publish below, so a published wrapper always calls the latest getToken.
  useLayoutEffect(() => {
    getTokenRef.current = getToken;
  });
  useLayoutEffect(() => {
    onAuth({ isLoaded, isSignedIn, getToken: stableGetToken, orgId, orgRole, userId, audIsConvex });
  }, [onAuth, stableGetToken, isLoaded, isSignedIn, orgId, orgRole, userId, audIsConvex]);
  return null;
}

/** Clerk auth as published by the bridge; null until it commits (treat as loading). */
export function useClerkAuthSnapshot() {
  return useContext(ClerkAuthSnapshotContext);
}

const INITIAL_RETRY_MS = 2_000;
const MAX_RETRY_MS = 60_000;

type FetchAccessToken = (args: { forceRefreshToken: boolean }) => Promise<string | null>;
type AuthIdentity = readonly [
  getToken: ClerkAuth["getToken"] | undefined,
  orgId: string | null | undefined,
  orgRole: string | null | undefined,
  userId: string | null | undefined,
  recoveryEpoch: number,
];

function createFetchAccessToken(
  getToken: ClerkAuth["getToken"] | undefined,
  audienceIsConvex: boolean,
): FetchAccessToken {
  return async ({ forceRefreshToken }) => {
    if (!getToken) return null;
    try {
      if (audienceIsConvex) {
        return await getToken({ skipCache: forceRefreshToken });
      }
      return await getToken({ template: "convex", skipCache: forceRefreshToken });
    } catch {
      return null;
    }
  };
}

const sameIdentity = (a: AuthIdentity, b: AuthIdentity) =>
  a.every((value, index) => Object.is(value, b[index]));

// Mirrors convex/src/react-clerk/ConvexProviderWithClerk.tsx, with viewer and recovery triggers.
function useAuthFromClerk() {
  const recoveryEpoch = useContext(RecoveryEpochContext);
  const snapshot = useClerkAuthSnapshot();
  const { isLoaded = false, isSignedIn, getToken, orgId, orgRole, userId, audIsConvex = false } =
    snapshot ?? {};
  // A new fetchAccessToken triggers setAuth, so it is rebuilt only when the
  // identity changes. getToken is the bridge's stable wrapper, so it only
  // changes when the first snapshot lands.
  const identity: AuthIdentity = [getToken, orgId, orgRole, userId, recoveryEpoch];
  const [tokenFetcher, setTokenFetcher] = useState(() => ({
    identity,
    fetchAccessToken: createFetchAccessToken(getToken, audIsConvex),
  }));
  if (!sameIdentity(tokenFetcher.identity, identity)) {
    setTokenFetcher({ identity, fetchAccessToken: createFetchAccessToken(getToken, audIsConvex) });
  }

  return {
    isLoading: !isLoaded,
    isAuthenticated: isSignedIn ?? false,
    fetchAccessToken: tokenFetcher.fetchAccessToken,
  };
}

function ConvexAuthRecovery({ retry }: { retry: () => void }) {
  const isSignedIn = useClerkAuthSnapshot()?.isSignedIn;
  const { isLoading, isAuthenticated } = useConvexAuth();
  const retryDelay = useRef(INITIAL_RETRY_MS);

  useEffect(() => {
    if (!isSignedIn || isAuthenticated) {
      retryDelay.current = INITIAL_RETRY_MS;
    }
    if (!isSignedIn || isLoading || isAuthenticated) return;

    const triggerRetry = () => {
      retryDelay.current = Math.min(retryDelay.current * 2, MAX_RETRY_MS);
      retry();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") triggerRetry();
    };
    const timer = window.setTimeout(triggerRetry, retryDelay.current);
    window.addEventListener("online", triggerRetry);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("online", triggerRetry);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [isAuthenticated, isLoading, isSignedIn, retry]);

  return null;
}

// ConvexProviderWithAuth takes its auth hook as a prop and calls it
// unconditionally on every render; the non-hook alias keeps React Compiler from
// treating that hand-off as a conditional hook call.
const authFromClerk = useAuthFromClerk;

export function ConvexClerkAuthProvider({
  children,
  client,
}: {
  children: ReactNode;
  client: Parameters<typeof ConvexProviderWithAuth>[0]["client"];
}) {
  const [recoveryEpoch, setRecoveryEpoch] = useState(0);
  const retry = useCallback(() => setRecoveryEpoch((epoch) => epoch + 1), []);
  const [clerkAuth, setClerkAuth] = useState<ClerkAuthSnapshot | null>(null);

  return (
    <ClerkAuthSnapshotContext.Provider value={clerkAuth}>
      <Suspense fallback={null}>
        <ClerkAuthBridge onAuth={setClerkAuth} />
      </Suspense>
      <RecoveryEpochContext.Provider value={recoveryEpoch}>
        <ConvexProviderWithAuth client={client} useAuth={authFromClerk}>
          <ConvexAuthRecovery retry={retry} />
          {children}
        </ConvexProviderWithAuth>
      </RecoveryEpochContext.Provider>
    </ClerkAuthSnapshotContext.Provider>
  );
}
